// The whole tree: menus → options → departments, plus after-hours branches.

import { useGuide } from './context';
import { nodeName } from './model';
import { SectionHead, Card } from './components';
import type { CallFlowNode } from '../../api/client';

export function StructureSection() {
  const { t, flow, model, lang, navigate, accent } = useGuide();

  function renderNode(id: string, keyLabel: string, trail: Set<string>, depth: number): JSX.Element | null {
    const node = model.byId.get(id);
    if (!node) return null;
    const isMenu = node.type === 'autoAttendant';
    const isQueue = node.type === 'queue';
    const cyclic = trail.has(id);
    const open = () => (isQueue ? navigate({ section: 'departments', id }) : isMenu ? navigate({ section: 'callflow', id }) : undefined);
    const children: Array<{ id: string; key: string }> = [];
    if (isMenu && !cyclic) {
      for (const m of node.menu) children.push({ id: m.targetId, key: m.key });
      if (node.operatorTargetId && !node.menu.some(m => m.key === '0')) children.push({ id: node.operatorTargetId, key: '0' });
    }
    const nextTrail = new Set(trail); nextTrail.add(id);
    return (
      <li key={`${keyLabel}-${id}-${depth}`} className="relative">
        <div className="flex items-center gap-2 py-1">
          {keyLabel && <span className="w-6 h-6 rounded text-[11px] font-bold text-white flex items-center justify-center shrink-0" style={{ backgroundColor: accent }}>{keyLabel}</span>}
          <button type="button" onClick={open} disabled={!isMenu && !isQueue}
            className={`text-sm text-left ${isQueue ? 'font-medium text-gray-900' : isMenu ? 'text-gray-800' : 'text-gray-500'} ${isMenu || isQueue ? 'hover:underline' : ''}`}>
            {nodeName(node, lang)}
          </button>
          <span className="text-[10px] text-gray-400">{isMenu ? t('autoAttendant') : isQueue ? t('callQueue') : terminalLabel(node)}</span>
          {cyclic && <span className="text-[10px] text-gray-400">↺</span>}
        </div>
        {children.length > 0 && (
          <ul className="ml-3 pl-3 border-l border-gray-200">
            {children.map(c => renderNode(c.id, c.key, nextTrail, depth + 1))}
          </ul>
        )}
        {isMenu && !cyclic && node.afterHoursTargetId && (
          <ul className="ml-3 pl-3 border-l border-dashed border-amber-300">
            <li className="text-[10px] text-amber-700 pt-1">{t('afterHours')}</li>
            {renderNode(node.afterHoursTargetId, '', nextTrail, depth + 1)}
          </ul>
        )}
      </li>
    );
  }

  function terminalLabel(n: CallFlowNode): string {
    return n.type === 'voicemail' ? t('voicemail') : n.type === 'disconnect' ? t('disconnect') : n.type === 'external' ? t('forwardTo') : t('person');
  }

  return (
    <div className="space-y-3">
      <SectionHead title={t('structureTitle')} intro={t('structureIntro')} />
      <Card className="p-4 overflow-x-auto">
        <div className="text-xs text-gray-500 mb-1">{t('mainNumber')} · {flow.mainNumbers.join(', ')}</div>
        <ul>{renderNode(flow.openTargetId, '', new Set(), 0)}</ul>
        {flow.closedTargetId !== flow.openTargetId && (
          <div className="mt-3 pt-3 border-t border-dashed border-amber-300">
            <div className="text-[10px] text-amber-700 mb-1">{t('afterHours')}</div>
            <ul>{renderNode(flow.closedTargetId, '', new Set(), 0)}</ul>
          </div>
        )}
      </Card>
    </div>
  );
}
